import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Send, Plus, Users, Circle } from "lucide-react";
import { toast } from "sonner";

export default function MessengerPanel({ accent }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [newPart, setNewPart] = useState("");
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  const loadThreads = async () => {
    const r = await api.get("/messenger/threads");
    setThreads(r.data);
    if (!active && r.data.length > 0) setActive(r.data[0]);
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/messenger/threads/${active.id}/messages`).then((r) => setMessages(r.data));
    // Open WebSocket for realtime
    const token = localStorage.getItem("sanaa_token");
    const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/ws/messenger?token=${encodeURIComponent(token)}&thread_id=${active.id}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message") {
          setMessages((m) => m.some((x) => x.id === data.id) ? m : [...m, {
            id: data.id, thread_id: data.thread_id, sender_id: data.sender_id,
            sender_name: data.sender_name, content: data.content, created_at: data.created_at,
          }]);
        }
      } catch {}
    };
    return () => { ws.close(); };
  }, [active?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const createThread = async () => {
    const emails = newPart.split(",").map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return;
    try {
      const r = await api.post("/messenger/threads", { participant_emails: emails });
      setNewPart(""); await loadThreads(); setActive(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create thread");
    }
  };

  const sendMsg = async () => {
    if (!input.trim() || !active || sending) return;
    setSending(true);
    try {
      await api.post(`/messenger/threads/${active.id}/messages`, { content: input.trim() });
      setInput("");
    } catch (e) { toast.error("Send failed"); }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]" data-testid="falcon-messenger">
      {/* Threads list */}
      <aside className="rounded-sm border border-border/60 bg-card overflow-hidden flex flex-col">
        <div className="p-3 border-b border-border/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <Users className="h-3 w-3" /> New thread
          </div>
          <div className="flex gap-1">
            <input value={newPart} onChange={(e) => setNewPart(e.target.value)} placeholder="emails, comma-separated"
              data-testid="msg-new-emails"
              className="flex-1 h-8 rounded-sm border border-border bg-background px-2 text-xs" />
            <button onClick={createThread} data-testid="msg-create-thread"
              className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-white" style={{ background: accent }}>
              <Plus className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && <div className="p-4 text-xs text-muted-foreground">No threads yet. Start one above.</div>}
          {threads.map((t) => (
            <button key={t.id} onClick={() => setActive(t)} data-testid={`msg-thread-${t.id}`}
              className={`w-full text-start px-3 py-2.5 border-b border-border/40 hover:bg-secondary/40 transition-colors ${active?.id === t.id ? "bg-secondary/60" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm truncate">{t.title}</div>
                {(t.online_participants || []).length > 0 && (
                  <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                )}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{t.last_preview || "—"}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.last_message_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <main className="lg:col-span-2 rounded-sm border border-border/60 bg-card overflow-hidden flex flex-col">
        {active ? (
          <>
            <header className="px-4 py-3 border-b border-border/60">
              <div className="font-medium text-sm">{active.title}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                {active.participants?.map((p) => p.name).join(" · ")} · Encrypted at rest
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[70%] rounded-sm px-3 py-2 text-sm ${mine ? "text-white" : "bg-secondary"}`}
                      style={mine ? { background: accent } : undefined}>
                      {!mine && <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.sender_name}</div>}
                      <div>{m.content}</div>
                      <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); sendMsg(); }} className="border-t border-border/60 p-3 flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…"
                data-testid="msg-input"
                className="flex-1 h-9 rounded-sm border border-border bg-background px-3 text-sm" />
              <button type="submit" disabled={sending} data-testid="msg-send"
                className="h-9 px-3 rounded-sm text-white disabled:opacity-60" style={{ background: accent }}>
                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a thread or start a new one
          </div>
        )}
      </main>
    </div>
  );
}
